import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { AuthContext, type AccountSession, type AuthContextValue } from './authContext';
import { accountApiBase, requestEmailRegister } from './emailRegister';
import { getDeviceId } from './deviceId';

const AUTH_KEY = 'cyber-movies:auth:v1';

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  role: AccountSession['role'];
  uid: string;
  email: string | null;
  deviceId: string;
}

function tokenExpiry(token: string): number {
  try {
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function readStoredSession(): AccountSession | null {
  try {
    const raw = window.localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AccountSession;
    if (!session.refreshToken || session.deviceId !== getDeviceId()) return null;
    return session;
  } catch {
    return null;
  }
}

function storeSession(response: AuthResponse): AccountSession {
  const session: AccountSession = {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    expiresAt: tokenExpiry(response.access_token),
    uid: response.uid,
    email: response.email,
    role: response.role,
    deviceId: response.deviceId,
  };
  window.localStorage.setItem(AUTH_KEY, JSON.stringify(session));
  return session;
}

async function authRequest<T>(apiBase: string, path: string, init: RequestInit): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('X-Device-ID', getDeviceId());
  const response = await fetch(`${apiBase.replace(/\/+$/, '')}${path}`, { ...init, headers });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { message?: string | string[] } | null;
    const message = Array.isArray(payload?.message) ? payload.message.join(', ') : payload?.message;
    throw new Error(message || `Authorization failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export function AuthProvider({ apiBase, children }: { apiBase: string; children: ReactNode }) {
  const [authState, setAuthState] = useState<Pick<AuthContextValue, 'session' | 'status'>>(() => {
    const session = readStoredSession();
    return {
      session,
      status: !session ? 'anonymous' : session.expiresAt > Date.now() + 60_000 ? 'authenticated' : 'loading',
    };
  });
  const { session, status } = authState;

  const applyResponse = useCallback((response: AuthResponse) => {
    const next = storeSession(response);
    setAuthState({ session: next, status: 'authenticated' });
  }, []);

  const clearSession = useCallback(() => {
    window.localStorage.removeItem(AUTH_KEY);
    setAuthState({ session: null, status: 'anonymous' });
  }, []);

  const refresh = useCallback(async (current: AccountSession) => {
    try {
      const response = await authRequest<AuthResponse>(apiBase, '/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: current.refreshToken, deviceId: current.deviceId }),
      });
      applyResponse(response);
    } catch {
      clearSession();
    }
  }, [apiBase, applyResponse, clearSession]);

  useEffect(() => {
    if (!session || status !== 'loading') return;
    const timer = window.setTimeout(() => void refresh(session), 0);
    return () => window.clearTimeout(timer);
  }, [refresh, session, status]);

  useEffect(() => {
    if (!session || status !== 'authenticated') return;
    const delay = Math.max(1_000, session.expiresAt - Date.now() - 60_000);
    const timer = window.setTimeout(() => void refresh(session), delay);
    return () => window.clearTimeout(timer);
  }, [refresh, session, status]);

  const exchangeFirebaseUser = useCallback(async (user: User) => {
    const idToken = await user.getIdToken();
    const response = await authRequest<AuthResponse>(apiBase, '/auth/firebase', {
      method: 'POST',
      body: JSON.stringify({ idToken, deviceId: getDeviceId() }),
    });
    applyResponse(response);
  }, [apiBase, applyResponse]);

  const signIn = useCallback(async (email: string, password: string) => {
    const [{ signInWithEmailAndPassword, signOut: firebaseSignOut }, { firebaseAuth, firebaseReady }] = await Promise.all([
      import('firebase/auth'),
      import('./firebase'),
    ]);
    await firebaseReady;
    const credential = await signInWithEmailAndPassword(firebaseAuth, email, password);
    try { await exchangeFirebaseUser(credential.user); }
    catch (error) { await firebaseSignOut(firebaseAuth); throw error; }
  }, [exchangeFirebaseUser]);

  const register = useCallback(async (email: string, password: string) => {
    const customToken = await requestEmailRegister(
      accountApiBase(import.meta.env.VITE_ACCOUNT_API_URL),
      email,
      password,
    );
    const [{ signInWithCustomToken, signOut: firebaseSignOut }, { firebaseAuth, firebaseReady }] = await Promise.all([
      import('firebase/auth'),
      import('./firebase'),
    ]);
    await firebaseReady;
    const credential = await signInWithCustomToken(firebaseAuth, customToken);
    try { await exchangeFirebaseUser(credential.user); }
    catch (error) { await firebaseSignOut(firebaseAuth); throw error; }
  }, [exchangeFirebaseUser]);

  const signOut = useCallback(async () => {
    const current = session;
    clearSession();
    if (current) {
      await fetch(`${apiBase.replace(/\/+$/, '')}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${current.accessToken}`, 'X-Device-ID': current.deviceId },
      }).catch(() => undefined);
    }
    const [{ signOut: firebaseSignOut }, { firebaseAuth, firebaseReady }] = await Promise.all([
      import('firebase/auth'),
      import('./firebase'),
    ]);
    await firebaseReady;
    await firebaseSignOut(firebaseAuth).catch(() => undefined);
  }, [apiBase, clearSession, session]);

  const value = useMemo<AuthContextValue>(() => ({ status, session, signIn, register, signOut }), [register, session, signIn, signOut, status]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
